import {
	Component,
	OnInit,
	OnDestroy,
	Inject,
	Injector,
	HostListener,
	ViewEncapsulation,
	ChangeDetectorRef,
	AfterViewInit
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, NavigationStart, NavigationError, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Subscription, fromEvent, BehaviorSubject, firstValueFrom } from 'rxjs';
import { SecurityService } from './core/services/security.service';
import { ApplicationModel, AppMenuItem } from '@nx-ahs/shared/models';
import { AppMenuService, NotificationService, UtilitiesService } from '@nx-ahs/shared/services';
import { AppIdGuardService } from '@nx-ahs/shared/guards';
import { navigationDefaultAnimation } from '@nx-ahs/shared/animations';
import { IAppNotification } from '@nx-ahs/shared/interfaces';
import { CountupTimerService } from '@nx-ahs/ahs-timer';

import { environment } from '../environments/environment';
import { debounceTime } from 'rxjs/operators';
import { BrowserNotificationService } from './core/services/browser-notification.service';
import { BroadcastNotificationService } from './core/services/broadcast-notification.service';
import { SignalrService } from './core/services/signalr.service';
import { TaskService } from './core/services/task.service';
import { DynamicScriptingOptionsService, DynamicScriptingService } from '@nx-ahs/script-shared/services';
import { EncounterService } from './core/services/encounter.service';
import { Encounter } from './shared/models/enctr/encounter';
import { UserMenuPreferencesService } from './core/services/user-menu-preferences.service';
import { UserMenuHistoryService } from './core/services/user-menu-history.service';
import { TimeoutService } from '@nx-ahs/timeout';
import { BreadcrumbService } from './core/services/breadcrumb.service';
import { RpaService } from './core/services/rpa.service';
import { FormlyOptionsService, FormlyService } from '@nx-ahs/formly/shared';
import { UserSettingsService } from './core/services/user-settings.service';
import { User } from './modules/admin/models/user.model';
import { FeatureFlagService } from '@nx-ahs/feature-flags';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	encapsulation: ViewEncapsulation.None,
	animations: [navigationDefaultAnimation],
	standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
	//#region Private Variables
	private subscriptions: Subscription[] = [];
	private authInitialized = false;

	//#endregion

	//#region Public Variables
	public app: ApplicationModel;
	public appSubscription: Subscription;
	public applications: ApplicationModel[] = [];
	public appMenuConfig: AppMenuItem[] = [];
	public appMenuSubscription: Subscription;
	public environment: any = environment;
	public isMultiAppUser: boolean = false;
	public leftSidenavOpened: boolean = false;
	public rightSidenavOpened: boolean = false;
	public notifications: IAppNotification[] = [];
	public searchText: string;
	public showNewCall: boolean = false;
	public showNewInPerson: boolean = false;
	public showNotifications: boolean = false;
	public showSearch: boolean = false;
	public showSettings: boolean = false;
	public showTools: boolean = false;
	public altInPersonLabel: string;
	public unreadNotificationCount: number = 0;
	public user: User = null;
	public userSubscription: Subscription;
	public windowWidth: number;
	public encounterInProgress = false;
	public hideResumeEncounter = false;
	public roleBasedEncounters = false;
	public encounterRoles: any = null;
	public loading = true;
	public taskInboxMenuConfig: AppMenuItem[] = [];
	public appMenuMode: any = 'over';
	private clickTimeout: any;

	public sidenavExpanded: boolean = false;
	public iconStyle: any = ['fass', 'heartbeat'];
	public size: string = '3x';
	public primaryColor: string = 'primary-color';
	public secondaryColor: string = 'white-color';
	public menuColor: string = 'background-primary';
	public nonProdMessage: string = 'DO NOT USE THIS ENVIRONMENT FOR LIVE INTERACTIONS';
	public browserTitle: string = 'HT - ';
	public currentUrl: any = '';
	public isLoggedIn: boolean = false;
	public isRightNavExpanded = false;
	public userActive: boolean = false;
	public userActivityPollingInterval: any = null;
	public showUserActivityBanner: boolean = false;
	public isRefreshingActivity: boolean = false;
	public lastActivityRefreshTime: number = 0;
	public attendedRPAUserInactiveMessage: string = 'Your automated processes are not currently active';
	//#endregion

	//#region Constructor

	constructor(
		private notificationService: NotificationService,
		private appMenuService: AppMenuService,
		private appIdGuardService: AppIdGuardService,
		private userMenuPreferencesService: UserMenuPreferencesService,
		private userMenuHistoryService: UserMenuHistoryService,
		private userSettingsService: UserSettingsService,
		private router: Router,
		private securityService: SecurityService,
		private dialogService: MatDialog,
		private activatedRoute: ActivatedRoute,
		private browserNotificationService: BrowserNotificationService,
		private broadcastNotificationService: BroadcastNotificationService,
		private signalrService: SignalrService,
		private taskService: TaskService,
		private utilitiesService: UtilitiesService,
		private encounterService: EncounterService,
		private injector: Injector,
		private dynamicScriptingService: DynamicScriptingService,
		private cdr: ChangeDetectorRef,
		private timeoutService: TimeoutService,
		private rpaService: RpaService,
		@Inject('clientConfig') private clientConfig: BehaviorSubject<any>,
		private formlyService: FormlyService,
		private countupTimerService: CountupTimerService,
		private breadcrumbService: BreadcrumbService,
		private featureFlagService: FeatureFlagService
	) {
		// Keep track of every time a route changes to prevent application timeout
		const routerEventsSubscription = this.router.events.subscribe((event) => this.handleRouteChange(event));
		this.subscriptions.push(routerEventsSubscription);
	}

	//#endregion

	//#region Lifecycle Events

	ngOnInit() {
		if (!environment.production) {
			this.primaryColor = 'white-color';
			this.secondaryColor = 'primary-color';
			this.menuColor = 'background-white';
			this.browserTitle = 'HT ' + environment.environmentName + ' - ';
		}

		this.windowWidth = window.innerWidth;

		// Setup subscriptions to listen for changes
		this.setupResizeSubscription();
		this.setupUserSubscription();
		this.setupAppSubscription();
		this.setupNotificationSubscription();
		this.setupAppMenuSubscription();
		this.setupTimeoutSubscription();
		this.setupEncounterSubscription();
		this.setupTaskMenuSubscription();
		this.setupUserChangedSubscription();
		this.setupTaskInboxChangedSubcription();

		// Initialize the security manager (sets the current user if the user is still authenticated)
		this.securityService.initialize().then(() => {
			// Code that should only run if the user is already authenticated
			this.authenticatedInit();
		});
	}

	ngAfterViewInit() {
		//binding timer functions to encounter service
		this.encounterService.startTimer = () => {
			this.countupTimerService.startTimer();
		};
		this.encounterService.stopTimer = () => {
			this.countupTimerService.stopTimer();
		};
		this.encounterService.pauseTimer = () => {
			this.countupTimerService.pauseTimer();
		};
		this.encounterService.isTimerStarted = (): boolean => {
			return this.countupTimerService.isTimerStart;
		};
	}

	ngOnDestroy() {
		this.unsubscribeAll();
	}

	@HostListener('window:load', ['$event'])
	onLoaded(event) {
		const timeoutTime: number = parseInt(sessionStorage.getItem(this.securityService.timeoutName));
		const currentTime = Math.floor(Date.now() / 1000); // get unix timestamp, in seconds
		if (
			(!isNaN(timeoutTime) && timeoutTime > 0 && currentTime > timeoutTime) ||
			(!this.securityService.isAuthenticated() && !isNaN(timeoutTime))
		) {
			// force logout if over timeout time OR if authentication token has expired while tab is sleeping
			this.securityService.logout(); // log out without warning
		}
	}
	//#endregion

	//#region Public Methods

	public userProfile(): void {
		this.router.navigate(['security/user-profile']);
	}

	public appClicked(): void {
		const testholder = 'Test';
		//this.securityService.actionTaken();
		//this.router.navigate(['/application-verification/checklist']);
	}

	public dismissNotification(notification: IAppNotification, event: MouseEvent): void {
		const notificationCount = this.notifications.length;
		this.notificationService.removeNotification(notification);

		// Don't close the notification menu if more than one notification was in the list when dismiss was clicked
		if (notificationCount > 1) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	public dismissAllNotifications(): void {
		this.notificationService.removeAllNotifications();
	}

	//expand the sidenav to fullscreen or set it to its original width
	public expandRightSidenav(): void {
		this.sidenavExpanded = !this.sidenavExpanded;
	}

	public goHome(): void {
		this.appMenuService.setActiveMenuItem(null);
		this.resetMenu(this.appMenuConfig);
		this.toggleLeftSidenav(true);
		this.breadcrumbService.resetBreadcrumbArray();
		this.router.navigate(['']);
	}

	public logout(): void {
		this.securityService.logout();
	}

	public markRead(): void {
		this.notificationService.markAllRead();
	}

	public menuItemClicked(mi: AppMenuItem): void {
		if (!mi?.childMenu) {
			this.leftSidenavOpened = this.appMenuMode == 'over' ? false : true;
		}
	}

	public newEncounter(primary?: number, secondary?: number): void {
		this.encounterService.getLatestCTIEncounterFor(this.user.userId).subscribe((encounter) => {
			if (encounter === null) {
				this.encounterService.initialize(primary, secondary);
			} else {
				this.encounterService.cti_initialize(Encounter.fromData(encounter[0]));
			}
		});
	}

	public returnToEncounter(): void {
		if (this.encounterInProgress) {
			//this.encounterService.initialize(
			//	this.encounterService.currentEncounter.primaryEncounterTypeId,
			//	this.encounterService.currentEncounter.secondaryEncounterTypeId
			//);
			this.encounterService.navTo(this.encounterService.currentPage().auxOutlet);
		}
	}

	public resumeEncounter(): void {
		this.router.navigate(['search/resumable-encounters']);
	}

	public notificationClick(notification: IAppNotification) {
		if (notification.sidebar) {
			this.router.navigate([{ outlets: { sidebar: [notification.sidebar, notification.sidebarParam] } }]);
		} else if (notification.url) {
			this.router.navigate([notification.url]);
		} else if (notification.click) {
			notification.click(null);
		}
	}

	public openSettings(): void {
		this.router.navigate([{ outlets: { sidebar: ['app-settings'] } }]);
	}

	public search(): void {
		if (this.searchText) {
			this.searchText = this.searchText.replace(/\s{2,}/g, ' ').trim();
			this.router.navigate(['search/quick-search', this.searchText]).then(() => {
				if (this.encounterService.callCaddyOpen && !this.encounterService.encounterIsActive) {
					this.toggleRightSidenav();
				}
			});
		}
	}

	public setApplication(application: ApplicationModel): void {
		this.unsubscribeAll();

		this.securityService.clearApp();

		this.securityService.setCurrentApp(application);
		this.appIdGuardService.redirect();

		window.location.href = ''; //Force a refresh so application insights can reset with the correct iKey
	}

	public toggleLeftSidenav(forceClose = false): void {
		clearTimeout(this.clickTimeout);

		this.clickTimeout = setTimeout(() => {
			//forceClose = true means something other than the main menu button is attempting to close the left sidenav
			//If the sidenav's mode is 'over', it should close like normal. If it's 'side', the sidenav should remain open.
			if (forceClose) {
				if (this.appMenuMode == 'over') {
					this.leftSidenavOpened = false;
					this.appMenuService.appMenuToggled.emit();
				}
			} else {
				//forceClose = false means the main menu button has been clicked.
				//If the sidenav's mode is 'side', it should be reset to 'over' and the sidenav should be closed.
				//Otherwise, toggle the sidenav's state like normal.
				if (this.appMenuMode == 'side') {
					this.appMenuMode = 'over';
				}

				this.leftSidenavOpened = !this.leftSidenavOpened;
			}
		}, 200);
	}

	public toggleLeftSidenavMode() {
		//If the user double clicks the main menu button while the sidenav is closed, change the sidenav's mode to 'side' (which will 'pin' it to the side) and open it.
		//If the user double clicks the button and the sidenav is open and set to 'side', reset the sidenav's mode and close it.
		//If the user double clicks the button and the sidenav is open but is set to 'over', update the mode to 'side' to pin the menu.
		clearTimeout(this.clickTimeout);
		this.appMenuMode = this.appMenuMode != 'side' ? 'side' : 'over';

		if (this.appMenuMode == 'side') {
			this.leftSidenavOpened = true;
		} else {
			this.leftSidenavOpened = !this.leftSidenavOpened;
		}

		this.appMenuService.appMenuToggled.emit();
	}

	public leftSidenavClosing() {
		this.appMenuService.clearText();
	}

	public toggleRightSidenav(): void {
		this.rightSidenavOpened = !this.rightSidenavOpened;
		this.encounterService.callCaddyOpen = this.rightSidenavOpened;

		if (this.rightSidenavOpened == false) {
			this.sidenavExpanded = false;
			this.taskService.fullscreenTask = false;
			this.router.navigate([{ outlets: { sidebar: null } }]);
			this.taskService.taskSidebarClosedEvent.emit();
		}

		if (this.taskService.fullscreenTask) {
			this.sidenavExpanded = true;
		}
	}

	public openUserSettings(): void {
		this.router.navigate(['security/user-settings']).then(() => {
			if (this.encounterService.callCaddyOpen) {
				this.toggleRightSidenav();
			}
		});
	}

	//#endregion

	//#region Private Methods

	private authenticatedInit(): void {
		// Call code that should run if the user is already authenticated when the app component initializes.  This code should also show up in the
		// subscriptions so that it runs when the user is not authenticated when the app component initializes.

		if (!this.authInitialized && this.securityService.isAuthenticated()) {
			this.app = this.securityService.getCurrentApp();
			this.user = this.securityService.getCurrentUser();
			this.isMultiAppUser = this.securityService.isMultiAppUser();
			this.unreadNotificationCount = this.notificationService.getUnreadCount();
			this.calculateShowTools();
			this.processConfig();
			this.browserNotificationService.getPermission();
			this.signalrService.initialize();
			this.getMyApplications();
			this.getSetClientConfig();
			this.initializeDynamicScripting();
			this.authInitialized = true;
			this.isLoggedIn = true;
			this.loading = false;
		} else {
			this.loading = false;
		}
	}

	private calculateShowTools(): void {
		if (this.user && this.app) {
			this.showTools = true;
		} else {
			this.showTools = false;
		}
	}

	private handleRouteChange(event: any): void {
		if (event instanceof NavigationStart) {
			// Store the navigation trigger, imperative = router.navigate, popstate - browser back/forward button
			this.utilitiesService.setNavigationTrigger(event.navigationTrigger);

			// Update last action date
			this.timeoutService.actionTaken();
		} else if (event instanceof NavigationEnd) {
			const route = this.activatedRoute.children.find((x) => {
				return x.outlet == 'sidebar';
			});

			// If the route has a sidebar route then handle it, otherwise make sure the sidebar is closed
			if (route) {
				// If the sidebar is being set to a route then make sure it's open, else if it's being set to null and it's open then close it
				if (route.snapshot.url && route.snapshot.url.length > 0 && !this.rightSidenavOpened) {
					this.toggleRightSidenav();
				} else if ((!route.snapshot || route.snapshot.url.length == 0) && this.rightSidenavOpened) {
					this.toggleRightSidenav();
				}
			} else if (this.rightSidenavOpened) {
				this.toggleRightSidenav();
			}

			//This is needed for breadcrumbs
			if (event.url != '') {
				this.currentUrl = event.url.replace('#main', '');
			} else {
				this.currentUrl = '';
			}
		} else if (event instanceof NavigationError) {
			const x = event; //This is just here to have a place to put a breakpoint.
		}
	}

	private initializeDynamicScripting(): void {
		const model = new DynamicScriptingOptionsService();
		model.apiUrl = environment.apiUrl;
		model.localTokenName = 'adal.idtoken';
		model.injector = this.injector;
		model.clientConfig = this.securityService?.getCurrentApp()?.application?.clientConfig;
		model.x_ht_appId = this.securityService.getCurrentApp()?.application?.applicationId?.toString();

		this.dynamicScriptingService.init(model);

		const fmodel = new FormlyOptionsService();
		fmodel.apiUrl = environment.apiUrl;
		fmodel.localTokenName = 'adal.idtoken';
		fmodel.injector = this.injector;
		fmodel.clientConfig = this.securityService?.getCurrentApp()?.application?.clientConfig;
		fmodel.x_ht_appId = this.securityService.getCurrentApp()?.application?.applicationId?.toString();

		this.formlyService.init(fmodel);
	}

	private processConfig(): void {
		if (this.app) {
			this.appMenuService.loadConfig(this.app.application.clientConfig.layout.menu);
			this.showNewCall = this.app.application.clientConfig.layout.header.showNewCall;
			this.showNewInPerson = this.app.application.clientConfig.layout.header.showNewInPerson;
			this.showNotifications = this.app.application.clientConfig.layout.header.showNotifications;
			this.showSearch =
				this.app.application.clientConfig.layout.header.showSearch &&
				this.app.application.clientConfig.layout.header.searchId;
			this.showSettings = this.app.application.clientConfig.layout.header.showSettings;
			this.hideResumeEncounter = this.app.application.clientConfig.layout.header.hideResumeEncounter;
			this.roleBasedEncounters = this.app.application.clientConfig.layout.header.roleBasedEncounters; // ?? false;

			if (this.roleBasedEncounters) {
				this.encounterRoles = this.app.application.clientConfig.layout.encounterRoles;
			}

			if (this.app.application.clientConfig.layout.header.altInPersonLabel)
				this.altInPersonLabel = this.app.application.clientConfig.layout.header.altInPersonLabel;

			this.userMenuHistoryService.getUserMenuHistoryItems();
			this.userMenuPreferencesService.getUserFavoritedMenuItems();

			this.userSettingsService.getUserSettings();

			// Check if RPA is enabled for this application and initialize if it is
			if (this.app.application.clientConfig.rpa?.useRPA) {
				this.rpaService.initializeAttendedProcesses();
				this.startUserActivityPolling();
			}
		}
	}

	private setupAppSubscription(): void {
		const appSubscription = this.securityService.appChanged.subscribe(async (app) => {
			this.authenticatedInit();
			this.app = app;
			this.calculateShowTools();
			this.processConfig();
			const config = await this.securityService.getClientConfig(app?.application?.applicationId);
			this.clientConfig.next(config);
			// Re-fetch feature flags now that x-ht-appId is available in the request header.
			// The APP_INITIALIZER fetch runs before app selection, so app-scoped filters
			// (e.g. ApplicationFilter) would evaluate against no app ID and return false.
			if (app) {
				await firstValueFrom(this.featureFlagService.getFeatureFlags());
			}
			if (this.securityService.isAuthenticated && this.app) {
				//used to make sure the signalR Hub connection is established

				setTimeout(async () => {
					return await this.getUnacknowledgedNotifications();
				}, 5000);
			}
			this.cdr.detectChanges();
			await this.loadTaskMenu();
		});

		this.subscriptions.push(appSubscription);
	}

	private setupAppMenuSubscription(): void {
		const appMenuSubscription = this.appMenuService.appMenuUpdated.subscribe((newMenuConfig: AppMenuItem[]) => {
			this.appMenuConfig = newMenuConfig;
		});

		this.subscriptions.push(appMenuSubscription);
	}

	private setupResizeSubscription(): void {
		// Start tracking window width to determine sidebar mode
		const resizeSubscription = fromEvent(window, 'resize')
			.pipe(debounceTime(500))
			?.subscribe((event) => {
				//@ts-ignore TypeScript wasn't recognizing innerWidth property
				this.windowWidth = event.target.innerWidth;
			});

		this.subscriptions.push(resizeSubscription);
	}

	private setupEncounterSubscription(): void {
		const encounterSubscription = this.encounterService.currentEncounterChanged.subscribe((val: Encounter) => {
			// used timeout to resolve ExpressionChangedAfterItHasBeenCheckedError
			setTimeout(() => {
				this.encounterInProgress = this.encounterService.isTimerStarted();
			});
		});

		this.subscriptions.push(encounterSubscription);
	}

	private setupNotificationSubscription(): void {
		// Update the notification array and unread count anytime the notifications change
		const notificationSubscription = this.notificationService.notificationsUpdated.subscribe((notifications) => {
			this.notifications = notifications;
			this.unreadNotificationCount = notifications.filter((x) => {
				return !x.isRead;
			}).length;
		});

		this.subscriptions.push(notificationSubscription);
	}

	private setupTimeoutSubscription(): void {
		const timeoutSubscription = this.timeoutService.setupTimeoutSubscription(this.dialogService, true);
		this.subscriptions.push(timeoutSubscription);
	}

	private setupUserSubscription(): void {
		const userSubscription = this.securityService.currentUserChanged.subscribe((user) => {
			this.user = user;
			this.isMultiAppUser = this.securityService.isMultiAppUser();
			this.calculateShowTools();
			this.browserNotificationService.getPermission();
			this.signalrService.initialize();
			this.initializeDynamicScripting();
		});

		this.subscriptions.push(userSubscription);
	}

	private setupTaskMenuSubscription(): void {
		const taskMenuSubscription = this.taskService.taskInboxData$.subscribe((newMenuConfig: AppMenuItem[]) => {
			this.taskInboxMenuConfig = newMenuConfig;

			this.addTaskMenu();
		});

		this.subscriptions.push(taskMenuSubscription);
	}

	private setupTaskInboxChangedSubcription(): void {
		const taskInboxChangedSubscription = this.taskService.menuItemClickedEvent.subscribe((itemClicked) => {
			//if the task details sidebar is expanded and we're switching inboxes, reset the sidebar to its default width
			if (this.sidenavExpanded == true) {
				this.sidenavExpanded = false;
				this.taskService.fullscreenTask = false;
			}
		});

		this.subscriptions.push(taskInboxChangedSubscription);
	}

	private setupUserChangedSubscription(): void {
		const currentUserChanged = this.securityService.currentUserChanged.subscribe(async (userModel: any[]) => {
			this.cdr.detectChanges();
			await this.loadTaskMenu();
		});

		this.subscriptions.push(currentUserChanged);
	}

	private async getUnacknowledgedNotifications(): Promise<void> {
		await firstValueFrom(this.broadcastNotificationService.resendUnacknowledgedBroadcastNotifications());
	}

	private async getSetClientConfig(): Promise<void> {
		// set clientconfig service for shared modules
		const clientConfig = await this.securityService?.getClientConfig();
		if (clientConfig) {
			this.clientConfig.next(clientConfig);
		}
	}

	private getMyApplications(): void {
		this.securityService.getMyApplications().then((data) => {
			this.applications = data;
			// sort applications alphabetically, with 'Demo' at the bottom
			this.applications.sort((a, b) =>
				a.application.name > b.application.name ? 1 : b.application.name > a.application.name ? -1 : 0
			);
			this.applications.push(
				this.applications.splice(
					this.applications.findIndex((x) => x.application.name == 'Demo'),
					1
				)[0]
			);
		});
	}

	private async loadTaskMenu(): Promise<void> {
		if (this.app && this.securityService.isAuthenticated() && this.securityService.getCurrentUser()) {
			const menuItem = this.appMenuConfig.find((a) => a.url == 'task-inbox');

			if (menuItem && menuItem.indexId) {
				await this.taskService.loadTaskInboxes(this.app, menuItem.indexId, false);
			}
		}
	}

	private addTaskMenu() {
		if (this.appMenuConfig?.length > 0 && this.taskInboxMenuConfig?.length > 0) {
			const appMenu = this.appMenuConfig;
			const taskMenu = appMenu.find((a) => a.url == 'task-inbox');
			if (taskMenu) {
				taskMenu.childMenu = this.taskInboxMenuConfig;
				this.appMenuService.loadConfig(appMenu);
			}
		}
	}

	private resetMenu(menu: AppMenuItem[]) {
		menu.forEach((mi: AppMenuItem) => {
			mi.expanded = false;
			if (mi.childMenu) this.resetMenu(mi.childMenu);
		});
	}

	public setSkipFocus(): void {
		const element = document.querySelector('#main');
		const focusable = element.querySelectorAll(
			'button:not([aria-hidden="true"]):not([disabled="true"]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
		);
		const firstFocusable = focusable[0];
		(firstFocusable as HTMLElement).focus();
	}

	private unsubscribeAll(): void {
		this.subscriptions.forEach((s) => {
			s.unsubscribe();
		});
		if (this.userActivityPollingInterval) {
			clearInterval(this.userActivityPollingInterval);
		}
	}

	public async refreshUserActivity(): Promise<void> {
		const now = Date.now();
		const timeSinceLastRefresh = now - this.lastActivityRefreshTime;

		// Enforce 5-second cooldown
		if (timeSinceLastRefresh < 5000) {
			return;
		}

		this.isRefreshingActivity = true;
		this.lastActivityRefreshTime = now;

		try {
			await this.checkUserActivity();
		} finally {
			this.isRefreshingActivity = false;
		}
	}

	public isRefreshButtonDisabled(): boolean {
		if (this.isRefreshingActivity) {
			return true;
		}

		const timeSinceLastRefresh = Date.now() - this.lastActivityRefreshTime;
		return timeSinceLastRefresh < 5000;
	}

	public getRefreshButtonTooltip(): string {
		if (this.isRefreshingActivity) {
			return 'Checking status...';
		}

		const timeSinceLastRefresh = Date.now() - this.lastActivityRefreshTime;
		if (timeSinceLastRefresh < 5000) {
			const secondsRemaining = Math.ceil((5000 - timeSinceLastRefresh) / 1000);
			return `Available in ${secondsRemaining}s`;
		}

		return 'Refresh status';
	}

	private async startUserActivityPolling(): Promise<void> {
		// Initial check
		const active = await this.rpaService.getIsAssistantActive(70);

		if (this.rpaService.isAssistantEnabled === false || active === null) {
			this.showUserActivityBanner = false;
			return;
		}

		this.checkUserActivity();

		// Poll every 30 seconds
		this.userActivityPollingInterval = setInterval(() => {
			this.checkUserActivity();
		}, 70000);
	}

	private async checkUserActivity(): Promise<void> {
		this.userActive = await this.rpaService.getIsAssistantActive(70);
		console.log('User activity status:', this.userActive);
		const clientConfig = await this.securityService.getClientConfig();
		this.attendedRPAUserInactiveMessage =
			clientConfig?.rpa?.attendedUserInactiveMessage || 'Your automated processes are not currently active';

		if (this.userActive === false) {
			this.showUserActivityBanner = true;
			return;
		}

		this.showUserActivityBanner = false;
	}

	//#endregion
}
